// @flow
import * as React from "react";

import Titles from "./Titles";
import EmptyColumns from "./EmptyColumns";
import AdditionalTitles from "./AdditionalTitles";
import TabContent from "./TabContent";

import styles from "./Tabs.scss";
import type { DropdownOptions } from "../Dropdown/Dropdown";

export type Tab = {
  title: () => React.Node,
  content?: () => React.Node,
  amount: ?number
};

type TabsProps = {
  initialActiveIndex: number,
  onChange: (selectedIndex: number) => void,
  children?: (selectedIndex: number) => React.Node,
  tabs: Array<Tab>
};

type TabsState = {
  activeIndex: number,
  visibleColumns: number
};

export const maxVisibleColumns = 5;

export const widthFor2Columns = 650;
export const widthFor3Columns = 850;
export const widthFor4Columns = 1100;

class Tabs extends React.Component<TabsProps, TabsState> {
  static defaultProps = {
    onChange: () => {}
  };

  state = {
    activeIndex: 0,
    visibleColumns: maxVisibleColumns
  };

  componentDidMount() {
    this.setInitialActiveIndex();
    this.calculateVisibleColumns();
    window.addEventListener("resize", this.handleWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleWindowResize);
  }

  handleWindowResize = () => {
    this.calculateVisibleColumns();
  };

  calculateVisibleColumns = () => {
    const width = window.innerWidth;

    let newVisibleColumns = maxVisibleColumns;

    if (width < widthFor2Columns) {
      newVisibleColumns = 2;
    } else if (width < widthFor3Columns) {
      newVisibleColumns = 3;
    } else if (width < widthFor4Columns) {
      newVisibleColumns = 4;
    }

    this.setState(({ visibleColumns: currentVisibleColumns }) => ({
      ...(currentVisibleColumns !== newVisibleColumns
        ? { visibleColumns: newVisibleColumns }
        : null)
    }));
  };

  setInitialActiveIndex = () => {
    const { initialActiveIndex } = this.props;

    if (initialActiveIndex) {
      this.setState({ activeIndex: initialActiveIndex });
    }
  };

  changeActiveIndex = (activeIndex: number) => {
    const { onChange } = this.props;
    onChange(activeIndex);

    this.setState({ activeIndex });
  };

  getTitlesToDisplay = (): Array<Tab> => {
    const { visibleColumns } = this.state;
    const { tabs } = this.props;

    return tabs.slice(0, visibleColumns);
  };

  getNumberOfEmptyColumns = () => {
    const { tabs } = this.props;
    const { visibleColumns } = this.state;

    const numberOfTabs = tabs.length;

    if (numberOfTabs >= visibleColumns) {
      return 0;
    }

    return visibleColumns - numberOfTabs;
  };

  getDropdownOptions = (numberOfVisibleColums: number): DropdownOptions => {
    const { tabs } = this.props;

    const tabsToDisplay = tabs.slice(numberOfVisibleColums);

    return tabsToDisplay.map(({ title, amount }, tabIndex) => {
      const content = title();

      return {
        text: () => (
          <span>
            {(amount || amount === 0) && <span className={styles.amountInDropdown}>{amount}</span>}
            {content}
          </span>
        ),
        title: content,
        onClick: closePopover => {
          this.changeActiveIndex(numberOfVisibleColums + tabIndex);
          closePopover();
        }
      };
    });
  };

  render() {
    const { children, tabs } = this.props;
    const { activeIndex, visibleColumns } = this.state;

    const titles = this.getTitlesToDisplay();
    const amountOfEmptyColumns = this.getNumberOfEmptyColumns();
    const dropdownOptions = this.getDropdownOptions(titles.length);

    if (!tabs.length) {
      return null;
    }

    return (
      <div className={styles.tabsContainer}>
        <div className={styles.titlesContainer}>
          <Titles
            tabs={titles}
            activeIndex={activeIndex}
            changeActiveIndex={this.changeActiveIndex}
          />
          <EmptyColumns amountOfEmptyColumns={amountOfEmptyColumns} />
          <AdditionalTitles
            dropdownOptions={dropdownOptions}
            dropdownActiveIndex={activeIndex - visibleColumns}
          />
        </div>
        <TabContent content={children ? () => children(activeIndex) : tabs[activeIndex].content} />
      </div>
    );
  }
}

export default Tabs;
